import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Input } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { setSelection, setSelectedOption, setITComment } from "../../../redux/actions/itServiceAction";
import { setITServiceDeskResponse } from "../../../redux/actions/feedbackActions";
import { useMediaQuery } from "react-responsive";
import "./itService.css"; 
const translations = {
  English: {
    title: "IT Service Desk",
    question: "Have you interacted with the IT Service desk lately?",
    yes: "Yes",
    no: "No",
    feedbackPrompt: "How was your interaction? Do you have any suggestions/feedback for the team? ",
    optionalText: "(optional)",
    feedbackPlaceholder: "Enter your feedback",
    thankYou: "Thank you",
    thankYouMessage: "We have noted your response."
  },
  Japanese: {
    title: "IT サービスデスク",
    question: "最近、ITサービスデスクとやり取りしましたか？",
    yes: "はい",
    no: "いいえ",
    feedbackPrompt: "やり取りはどうでしたか？ チームへの提案/フィードバックはありますか？",
    optionalText: "(任意)",
    feedbackPlaceholder: "フィードバックを入力してください",
    thankYou: "ありがとうございます",
    thankYouMessage: "ご回答を承りました。"
  },
  Chinese: {
    title: "IT 服务台",
    question: "您最近是否与 IT 服务台有过互动？",
    yes: "是",
    no: "否",
    feedbackPrompt: "您的互动体验如何？有什么建议/反馈给团队吗？",
    optionalText: "(可选)",
    feedbackPlaceholder: "请提供您的反馈",
    thankYou: "谢谢",
    thankYouMessage: "我们已记录您的反馈。"
  },
  Dutch: {
    title: "IT Service Desk",
    question: "Heeft u onlangs contact gehad met de IT Service Desk?",
    yes: "Ja",
    no: "Nee",
    feedbackPrompt: "Hoe was uw interactie? Heeft u suggesties/feedback voor het team? ",
    optionalText: "(optioneel)",
    feedbackPlaceholder: "Voer uw feedback in",
    thankYou: "Dank u",
    thankYouMessage: "We hebben uw reactie genoteerd."
  },
  German: {
    title: "IT-Service-Desk",
    question: "Haben Sie in letzter Zeit mit dem IT-Service-Desk interagiert?",
    yes: "Ja",
    no: "Nein",
    feedbackPrompt: "Wie war Ihre Interaktion? Haben Sie Vorschläge/Feedback für das Team? ",
    optionalText: "(optional)",
    feedbackPlaceholder: "Geben Sie Ihr Feedback ein",
    thankYou: "Vielen Dank",
    thankYouMessage: "Wir haben Ihre Rückmeldung notiert."
  },
  Spanish: {
    title: "Mesa de Servicio de IT",
    question: "¿Ha interactuado recientemente con la mesa de servicio de IT?",
    yes: "Sí",
    no: "No",
    feedbackPrompt: "¿Cómo fue su interacción? ¿Tiene alguna sugerencia o comentario para el equipo? ",
    optionalText: "(opcional)",
    feedbackPlaceholder: "Ingrese su comentario",
    thankYou: "Gracias",
    thankYouMessage: "Hemos registrado su respuesta."
  },
  French: {
    title: "Service Informatique",
    question: "Avez-vous récemment interagi avec le service informatique?",
    yes: "Oui",
    no: "Non",
    feedbackPrompt: "Comment s'est déroulée votre interaction ? Avez-vous des suggestions/commentaires pour l'équipe ? ",
    optionalText: "(facultatif)",
    feedbackPlaceholder: "Entrez votre commentaire",
    thankYou: "Merci",
    thankYouMessage: "Nous avons noté votre réponse."
  }
};
const ITServiceDesk = ({ onYesNoSelect, showThanks, setShowThanks }) => {
  const dispatch = useDispatch();
  const { yesNoSelectedOption, comment } = useSelector((state) => state.itService);
  const [yesSelected, setYesSelected] = useState(yesNoSelectedOption === "Yes");
  const [noSelected, setNoSelected] = useState(yesNoSelectedOption === "No");
  const [commentValue, setCommentValue] = useState(comment);
  const isMobile = useMediaQuery({ maxWidth: 768 }); 
  const selectedLanguage = useSelector((state) => state.language.selectedLanguage);
  const translation = translations[selectedLanguage];

  useEffect(() => {
    if (yesNoSelectedOption === "Yes") {
      setYesSelected(true);
      setNoSelected(false);
    } else if (yesNoSelectedOption === "No") {
      setYesSelected(false);
      setNoSelected(true);
    }
  }, [yesNoSelectedOption]);

  const handleYesClick = () => {
    const questionText = translations['English'].question;
    const response = "Yes";
  
    setYesSelected(true);
    setNoSelected(false);
    setShowThanks(false);
    onYesNoSelect(true);
    dispatch(setSelection(true));
    dispatch(setSelectedOption("Yes"));
  
    dispatch(
      setITServiceDeskResponse(
        "IT Service Desk", 
        "question1", 
        questionText, 
        response 
      )
    );
  };
  
  const handleNoClick = () => {
    const questionText = translations['English'].question;
    const response = "No";
  
    setYesSelected(false);
    setNoSelected(true);
    setShowThanks(true);
    onYesNoSelect(true);
    dispatch(setSelection(true));
    dispatch(setSelectedOption("No"));
  
    dispatch(
      setITServiceDeskResponse(
        "IT Service Desk", 
        "question1", 
        questionText, 
        response 
      )
    );
  };
  

  const handleCommentChange = (e) => {
    const newComment = e.target.value;
    const questionText = translations['English'].question;
  
    setCommentValue(newComment);
    dispatch(setITComment(newComment));   
    dispatch(
      setITServiceDeskResponse(
        "IT Service Desk", 
        "question2", 
        questionText, 
        yesNoSelectedOption, 
        newComment 
      )
    );
  };
  

  useEffect(() => {
    if (!yesSelected && !noSelected) {
      setShowThanks(false);
      onYesNoSelect(false);
      dispatch(setSelection(false));
      dispatch(setSelectedOption(null));
    }
  }, [yesSelected, noSelected, setShowThanks, dispatch]);

  return (
    <section>
      <div style={{ marginTop: "40px" }}>
        <h4>IT Service Desk</h4>
      </div>
      <div style={{ marginTop: "20px" }}>
        <h2>{translation.question}</h2>
        <p style={{ fontSize: "12px", color: "gray" }}>{translation.optional}</p>
      </div>
      <div style={{ marginTop: "20px" }}>
        <Button
          type={yesSelected ? "primary" : "default"}
          style={{ marginRight: "20px", width: "160px", height: "40px" }}
          onClick={handleYesClick}
          className="responsive-button"
        >
          {translation.yes}
        </Button>
        <Button
          type={noSelected ? "primary" : "default"}
          style={{ marginRight: "20px", width: "160px", height: "40px" }}
          onClick={handleNoClick}
          className="responsive-button"
        >
          {translation.no}
        </Button>
      </div>

      {yesSelected && (
        <div style={isMobile?{marginTop:"20px",marginRight:"5%"}:{marginTop:"20px"}}>
          <p>{translation.feedbackPrompt}</p>
          <p style={{ fontSize: "12px", color: "gray" }}>{translation.optionalText}</p>
          <Input.TextArea
            placeholder={translation.feedbackPlaceholder}
            style={{ height: "60px", resize: "none" }}
            value={commentValue}
            onChange={handleCommentChange}
          />
        </div>
      )}

      {showThanks && noSelected && (
        <div style={{
          marginTop: "20px",
          width: "320px",
          padding: "6px",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          backgroundColor: "#e6f9e6",
          textAlign: "center",
        }}>
          <CheckCircleOutlined style={{ color: "green", marginRight: "8px" }} />
          <span style={{ color: "green", fontWeight: "900" }}>{translation.thankYou}</span> <span style={{ color: "black" }}>{translation.thankYouMessage}</span>
        </div>
      )}
    </section>
  );
};

export default ITServiceDesk;
